import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { LanguageService } from 'src/app/config/language.service';
import { ICustomField, ICustomFieldForm } from '../../models/settings.models';
import {
  CUSTOM_FIELD_TYPES_OPTIONS,
  CUSTOM_FIELD_VISIBILITY_OPTIONS,
  CustomFieldTypes,
  CustomFieldType,
  CustomFieldVisibilityTypes,
  CustomFieldVisibilityType,
  RELATED_OBJECTS_OPTIONS,
} from '../../models/custom-fields.constants';
import { Actions, ofType } from '@ngrx/effects';
import { AddCustomField, AddCustomFieldSuccess, UpdateCustomField, UpdateCustomFieldSuccess } from '../../store/settings.actions';
import { Subject, take, takeUntil } from 'rxjs';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';

@Component({
  selector: 'app-add-custom-fields',
  templateUrl: './add-custom-fields.component.html',
  styleUrls: ['./add-custom-fields.component.scss']
})
export class AddCustomFieldsComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  readonly customFieldTypes = CustomFieldTypes;
  readonly customFieldVisibilityTypes = CustomFieldVisibilityTypes;
  readonly related_objects: SelectOption[] = RELATED_OBJECTS_OPTIONS.map(related_object => ({ label: this.translate.instant('custom_fields.related_objects.' + related_object), value: related_object }));
  readonly fieldTypeOptions: SelectOption[] = CUSTOM_FIELD_TYPES_OPTIONS.map((fieldType) => ({
    label: this.translate.instant('custom_fields.field_types.' + fieldType),
    value: fieldType,
  }));
  readonly visibilityOptions: SelectOption[] = [
    { label: this.translate.instant('common.all'), value: CustomFieldVisibilityTypes.ALL },
    ...CUSTOM_FIELD_VISIBILITY_OPTIONS.map((visibility) => ({
      label: this.translate.instant('custom_fields.visibility_options.' + visibility),
      value: visibility,
    })),
  ];

  languages: string[] = [
    'en',
    'fr',
    'it'
  ];

  allowed_values: string[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  currentLanguage: string = this.languageService.getCurrentLanguage();
  isUpdateMode: boolean = false;
  private isUpdatingVisibility = false;
  private lastVisibilitySelection: CustomFieldVisibilityType[] = [CustomFieldVisibilityTypes.ALL];
  private initialCustomField: ICustomField | null = null;
  private destroy$ = new Subject<void>();
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { customField?: ICustomField },
    private dialogRef: MatDialogRef<AddCustomFieldsComponent>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private store: Store<AppState>,
    private languageService: LanguageService,
    private actions$: Actions
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      object_type: [null, [Validators.required]],
      field_name: [null, [Validators.required]],
      field_type: [null, [Validators.required]],
      allowed_values: [[]],
      visibility: [[CustomFieldVisibilityTypes.ALL], [Validators.required]],
      is_required: [false],
      erp_mapping_key: [null],
      translations: this.fb.array([])
    });

    // At least one translations block: current language by default
    this.translationsArray.push(this.buildTranslationGroup(this.currentLanguage));

    // field_name drives label[current language key]
    this.form.get('field_name')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const v = (value ?? '').toString();
      const labelCtrl = this.translationsArray.at(0)?.get('label');
      if (labelCtrl && labelCtrl.value !== v) labelCtrl.setValue(v, { emitEvent: false });
    });

    // Keep allowed_values validators in sync with field_type
    this.form.get('field_type')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((fieldType: CustomFieldType) => {
      this.updateAllowedValuesValidators(fieldType ?? null);
    });
    this.updateAllowedValuesValidators(this.form.get('field_type')?.value);

    // visibility default + "all" behavior
    this.form.get('visibility')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: CustomFieldVisibilityType[] | null) => {
      if (this.isUpdatingVisibility) return;
      const arr = Array.isArray(value) ? value : [];
      const prev = this.lastVisibilitySelection ?? [];
      let next = arr;

      const hasAllNow = arr.includes(CustomFieldVisibilityTypes.ALL);
      const hadAllBefore = prev.includes(CustomFieldVisibilityTypes.ALL);

      if (hasAllNow && !hadAllBefore) {
        next = [CustomFieldVisibilityTypes.ALL];
      }
      else if (hasAllNow && hadAllBefore && arr.length > 1) {
        next = arr.filter((v) => v !== CustomFieldVisibilityTypes.ALL);
      }

      if (!next.length) next = [CustomFieldVisibilityTypes.ALL];
      if (next !== arr) {
        this.isUpdatingVisibility = true;
        this.form.get('visibility')?.setValue(next, { emitEvent: false });
        this.isUpdatingVisibility = false;
      }
      this.lastVisibilitySelection = next;
    });

    const injected = this.extractCustomFieldFromData(this.data);
    if (injected) {
      this.initialCustomField = injected;
      this.isUpdateMode = true;
      this.setFieldNameRequired(false);
      this.hydrateFormFromCustomField(injected);
      this.form.get('object_type')?.disable({ emitEvent: false });
    } else {
      this.setFieldNameRequired(true);
      this.form.get('object_type')?.enable({ emitEvent: false });
    }

    this.actions$
      .pipe(
        ofType(UpdateCustomFieldSuccess, AddCustomFieldSuccess),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
      this.cancel();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get translationsArray(): FormArray {
    return this.form.get('translations') as FormArray;
  }

  get translationsControls(): AbstractControl[] {
    return this.translationsArray?.controls ?? [];
  }

  get canAddLanguage(): boolean {
    return this.getUnusedLanguages().length > 0;
  }

  addAllowedValue(event: MatChipInputEvent): void {
    const input = (event.value || '').trim();
    if (input && !this.allowed_values.some((allowedValue) => allowedValue === input)) {
      const newAllowedValue = input;
      this.allowed_values.push(newAllowedValue);
      this.form.get('allowed_values')?.setValue([...this.allowed_values]);
    }
    event.chipInput?.clear();
  }

  removeAllowedValue(value: string): void {
    const next = this.allowed_values.filter((v) => v !== value);
    this.allowed_values = next;
    this.form.get('allowed_values')?.setValue([...this.allowed_values]);
    this.form.get('allowed_values')?.markAsDirty();
    this.form.get('allowed_values')?.updateValueAndValidity();
  }

  addLanguage(): void {
    const unused = this.getUnusedLanguages();
    if (!unused.length) return;
    this.translationsArray.push(this.buildTranslationGroup(unused[0]));
  }

  save(): boolean | void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const fieldType = raw.field_type as CustomFieldType;
    const primaryLang: string =
      this.translationsArray.at(0)?.get('language')?.value || this.currentLanguage;
    const fieldName: string = (raw.field_name ?? '').toString().trim();

    // label: required at least one key (field_name => label[primaryLang])
    const label: Record<string, string> = {};
    if (fieldName) label[primaryLang] = fieldName;
    for (let i = 1; i < this.translationsArray.length; i++) {
      const g = this.translationsArray.at(i);
      const lang = (g.get('language')?.value ?? '').toString();
      const v = (g.get('label')?.value ?? '').toString().trim();
      if (lang && v) label[lang] = v;
    }
    if (!Object.keys(label).length) return;

    // help_text: optional per language, omit empties
    const help_text: Record<string, string> = {};
    for (let i = 0; i < this.translationsArray.length; i++) {
      const g = this.translationsArray.at(i);
      const lang = (g.get('language')?.value ?? '').toString();
      const v = (g.get('help_text')?.value ?? '').toString().trim();
      if (lang && v) help_text[lang] = v;
    }

    const allowed_values =
      fieldType === CustomFieldTypes.SINGLE_SELECT || fieldType === CustomFieldTypes.MULTI_SELECT
        ? (raw.allowed_values ?? [])
        : [];
    const visibility =
      Array.isArray(raw.visibility) && raw.visibility.length
        ? (raw.visibility as CustomFieldVisibilityType[])
        : [CustomFieldVisibilityTypes.ALL];

    const payload: ICustomFieldForm = {
      object_type: raw.object_type,
      field_type: fieldType,
      label,
      help_text,
      allowed_values,
      visibility,
      is_required: !!raw.is_required,
      erp_mapping_key: raw.erp_mapping_key ? raw.erp_mapping_key : null,
    };

    if (this.isUpdateMode) {
      this.store.dispatch(UpdateCustomField({ id: this.initialCustomField!.id, customField: payload }));
    } else {
      this.store.dispatch(AddCustomField({ customField: payload }));
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  removeLanguage(index: number): void {
    if (index <= 0) return; // first block can't be deleted
    this.translationsArray.removeAt(index);
    this.translationsArray.markAsDirty();
    this.translationsArray.updateValueAndValidity();
  }

  isLanguageOptionDisabled(language: string, currentIndex: number): boolean {
    const selected = this.getSelectedLanguages();
    const current = this.translationsArray.at(currentIndex)?.get('language')?.value;
    if (language === current) return false;
    return selected.includes(language);
  }

  private buildTranslationGroup(language: string): FormGroup {
    return this.fb.group({
      language: [language, [Validators.required]],
      label: [''],
      help_text: [''],
    });
  }

  private extractCustomFieldFromData(data: any): ICustomField | null {
    const candidate: any = data?.custom_field ?? data?.customField ?? data;
    if (!candidate || typeof candidate !== 'object') return null;
    if (typeof candidate.object_type !== 'string') return null;
    if (typeof candidate.field_type !== 'string') return null;
    return candidate as ICustomField;
  }

  private hydrateFormFromCustomField(customField: ICustomField): void {
    const visibility =
      Array.isArray(customField.visibility) && customField.visibility.length
        ? customField.visibility
        : [CustomFieldVisibilityTypes.ALL];
    const cleanedVisibility =
      visibility.includes(CustomFieldVisibilityTypes.ALL) && visibility.length > 1
        ? visibility.filter((v) => v !== CustomFieldVisibilityTypes.ALL)
        : visibility;

    this.form.patchValue(
      {
        object_type: customField.object_type ?? null,
        field_type: customField.field_type ?? null,
        visibility: cleanedVisibility.length ? cleanedVisibility : [CustomFieldVisibilityTypes.ALL],
        is_required: !!customField.is_required,
        erp_mapping_key: customField.erp_mapping_key ?? null,
        allowed_values: Array.isArray(customField.allowed_values) ? customField.allowed_values : [],
      },
      { emitEvent: false }
    );

    // Keep chip list in sync
    this.allowed_values = Array.isArray(customField.allowed_values) ? [...customField.allowed_values] : [];

    // translations blocks: currentLanguage first, then any other languages present in label/help_text
    const labelObj = customField.label ?? {};
    const helpObj = customField.help_text ?? {};

    const otherLangs = new Set<string>();
    Object.keys(labelObj).forEach((k) => k && otherLangs.add(k));
    Object.keys(helpObj).forEach((k) => k && otherLangs.add(k));
    otherLangs.delete(this.currentLanguage);

    // rebuild translations array
    while (this.translationsArray.length) this.translationsArray.removeAt(0);

    const first = this.buildTranslationGroup(this.currentLanguage);
    this.translationsArray.push(first);

    // field_name is label[currentLanguage]; fall back to first available translation if missing
    const currentLabel = (
      labelObj?.[this.currentLanguage] || Object.values(labelObj).find((v) => !!v) || ''
    ).toString();
    this.form.get('field_name')?.setValue(currentLabel, { emitEvent: true });

    // other translations (editable)
    Array.from(otherLangs)
      .filter((l) => this.languages.includes(l))
      .forEach((lang) => {
        const g = this.buildTranslationGroup(lang);
        g.get('label')?.setValue((labelObj?.[lang] ?? '').toString(), { emitEvent: false });
        g.get('help_text')?.setValue((helpObj?.[lang] ?? '').toString(), { emitEvent: false });
        this.translationsArray.push(g);
      });

    // also keep current language help_text if provided
    first.get('help_text')?.setValue((helpObj?.[this.currentLanguage] ?? '').toString(), { emitEvent: false });

    // ensure validators are set for allowed_values based on current field_type
    this.updateAllowedValuesValidators(customField.field_type);
  }

  private getSelectedLanguages(): string[] {
    return this.translationsArray.controls
      .map((c) => (c.get('language')?.value ?? '').toString())
      .filter(Boolean);
  }

  private getUnusedLanguages(): string[] {
    const selected = new Set(this.getSelectedLanguages());
    return this.languages.filter((l) => !selected.has(l));
  }

  private updateAllowedValuesValidators(fieldType: CustomFieldType | null): void {
    const ctrl = this.form.get('allowed_values');
    if (!ctrl) return;

    if (
      fieldType === CustomFieldTypes.SINGLE_SELECT ||
      fieldType === CustomFieldTypes.MULTI_SELECT
    ) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      this.allowed_values = [];
      ctrl.setValue([], { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }


  private setFieldNameRequired(required: boolean): void {
    const ctrl = this.form.get('field_name');
    if (!ctrl) return;
    ctrl.clearValidators();
    if (required) ctrl.setValidators([Validators.required]);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }
}
